import {Platform} from 'react-native';
import {useNavigationState} from '@react-navigation/native';

import {AppRoute} from '../models/AppRoute';

import type {RouteSpec} from './routes/types';
import * as RootNavigation from './RootNavigation';

/**
 * DIS-1420: detach inactive native Screens only on web.
 *
 * On native (New-Architecture Fabric), detaching/re-attaching `Screen` views
 * during the post-order navigation (cross-navigator jump into CART_ORDER_PROCESSING
 * plus rapid tab switches) trips react-native-screens'
 * `addViewAt: View already has a parent` SoftException (software-mansion/react-native-screens
 * #3249/#2803), which corrupts the Fabric view tree and freezes touch.
 *
 * Web keeps `detachInactiveScreens` (stacked-DOM behaviour); the crash is
 * native-Fabric-only and web is unchanged by construction. Scroll-collapse is
 * detach-agnostic — see useScrollCollapse.ts (DIS-1127): the React tree persists
 * and native scroll position is preserved across detach/re-attach.
 *
 * Pure (takes Platform.OS as a defaulted arg) so it is unit-testable without
 * mocking the Platform module.
 */
export const shouldDetachInactiveScreens = (platformOS: string = Platform.OS): boolean =>
  platformOS === 'web';

export const isRouteEntryVisibleForLoggedInState = (isLoggedIn: boolean) => (route: RouteSpec) => {
  if (route.requiresLogin) {
    return isLoggedIn;
  }
  if (route.requiresLogout) {
    return !isLoggedIn;
  }
  return true;
};

/**
 * Check if a route exists in the current navigation stack
 * @returns
 */
export function useDoesRouteExist() {
  const routeNames = useNavigationState(state => state.routeNames);
  return (routeName: string) => routeNames.includes(routeName);
}

/**
 * Use this when we want to navigate to a route from anywhere in the app
 * React Navigation v7: Use RootNavigation for cross-navigator navigation
 * Check doesRouteExist in current stack first to not break existing functionality.
 *
 * Used in 3DS screens to navigate back to various targets after challenge completes
 */
export function useNavigate() {
  const doesRouteExist = useDoesRouteExist();

  return (routeName: string) => {
    if (doesRouteExist(routeName)) {
      RootNavigation.navigate(routeName as any);
    } else {
      // Navigate to nested route in ROOT_CHECKOUT_FLOW
      RootNavigation.navigate(AppRoute.ROOT_CHECKOUT_FLOW as any, {
        screen: routeName,
      });
    }
  };
}
